import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import ErrorBoundary from '../components/ErrorBoundary.jsx';
import { Shell } from '../components/Shell';
import Loader from '../components/Loader';
import { fetchCoverage, buildCoverageChips, nextStepFor } from '../components/EntryFlowModal';
import { MonthLedger } from '../components/MonthLedger';
import { X } from '../components/v2/Icons';
import { EntryStepper } from '../components/v2/EntryStepper';
import { CategoryContext } from '../components/v2/CategoryContext';
import { PageHeader, Button, Field, Input, Select, UnitInput, Callout } from '../components/v2/ui';
import { useT } from '../i18n';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5050';

const getCurrentMonth = () => {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  return `${year}-${month}`;
};

// Stationary combustion fuels only (excluding mobile/transport fuels)
const STATIONARY_FUEL_KEYS = [
  'diesel', 'gasoline', 'heating_oil', 'natural_gas', 'lpg',
  'wood_biomass', 'pellet_biomass', 'coal'
];

// Fuel to unit mapping for automatic unit selection
const FUEL_DEFAULT_UNITS = {
  'diesel': 'l',
  'gasoline': 'l',
  'heating_oil': 'l',
  'natural_gas': 'm3',
  'lpg': 'l',
  'wood_biomass': 'kg',
  'pellet_biomass': 'kg',
  'coal': 'kg'
};

/**
 * periodType is FIXED at '1' — one record is one billing month.
 *
 * The 3 / 6 / 12-month options are gone (data scientist, 2026-07-30: "annual
 * total but monthly insertions"). They never worked: the API ignores periodType,
 * so a "12 Mujore" entry filed a whole year's quantity into the start month and
 * skewed the monthly series, the coverage grid and inventory completeness.
 * Comparison in this product is baseline-year vs current year against the
 * reduction target, never a rolling 3- or 6-month window.
 *
 * Still sent in the payload so the request shape is unchanged.
 */
const MONTHLY_PERIOD_TYPE = '1';

// Feeds `activity_purpose` on scope1_records — how the fuel was actually used,
// so Scope 1 can be split by end-use in reports. Free text server-side (60 chars).
const ACTIVITY_PURPOSES = [
  'Ngrohje e ambientit',
  'Ujë i ngrohtë sanitar',
  'Nxehtësi procesi',
  'Gjenerator rezervë'
];

export const Scope1DjegjaPage = () => {
  const navigate = useNavigate();
  const { t } = useT();
  const [searchParams] = useSearchParams();

  const [formData, setFormData] = useState({
    buildingId: '',
    fuelKey: '',
    period: getCurrentMonth(),
    periodType: MONTHLY_PERIOD_TYPE,
    quantity: '205',
    unit: 'l'
  });

  const [facilities, setFacilities] = useState([]);
  const [fuels, setFuels] = useState([]);
  const [calculated, setCalculated] = useState(null);
  const [saving, setSaving] = useState(false);
  const [isSaved, setIsSaved] = useState(false);
  const [buildingValidationError, setBuildingValidationError] = useState('');
  const [summary, setSummary] = useState({ current: null, previous: null });
  // Zgjidh vendin → Shto të dhënat → Rishiko & regjistro
  const [step, setStep] = useState(0);
  const [mode, setMode] = useState('manual');
  const [activityPurpose, setActivityPurpose] = useState('');
  const [flowError, setFlowError] = useState('');
  const [coverage, setCoverage] = useState(null);
  const [ledgerKey, setLedgerKey] = useState(0);
  // Evidence document — attached through the same endpoint the process screen uses
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const [uploadedInvoice, setUploadedInvoice] = useState(null);

  const fetchSummary = async (period) => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_BASE}/api/emissions/scope1/summary?period=${period}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setSummary(data);
      }
    } catch (err) {
      console.error('Failed to fetch summary:', err);
    }
  };

  useEffect(() => {
    const token = localStorage.getItem('token');
    fetch(`${API_BASE}/api/buildings`, {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    })
      .then(res => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.json();
      })
      .then(data => setFacilities(Array.isArray(data) ? data : []))
      .catch(err => {
        console.error('Failed to fetch facilities:', err);
        setFacilities([]);
      });
  }, []);

  useEffect(() => {
    fetch(`${API_BASE}/api/fuels`)
      .then(res => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.json();
      })
      .then(data => {
        // Filter to only show stationary combustion fuels
        const stationaryFuels = (Array.isArray(data) ? data : [])
          .filter(f => STATIONARY_FUEL_KEYS.includes(f.key));
        setFuels(stationaryFuels);
      })
      .catch(err => {
        console.error('Failed to fetch fuels:', err);
        setFuels([]);
      });
  }, []);

  useEffect(() => {
    if (formData.period) {
      fetchSummary(formData.period);
    }
  }, [formData.period]);

  /**
   * Deep link from the action inbox: `?site=10&period=2026-03` prefills step 1
   * and drops the user straight on "Shto të dhënat", so closing a specific gap
   * takes one click instead of re-finding the site and month by hand.
   */
  useEffect(() => {
    const site = searchParams.get('site');
    const period = searchParams.get('period');
    if (!site && !period) return;
    setFormData(prev => ({
      ...prev,
      ...(site ? { buildingId: String(site) } : {}),
      ...(/^\d{4}-\d{2}$/.test(period || '') ? { period } : {})
    }));
    if (site) setStep(1);
  }, [searchParams]);


  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setIsSaved(false);
  };

  // Handle fuel change with automatic unit selection
  const handleFuelChange = (fuelKey) => {
    const defaultUnit = FUEL_DEFAULT_UNITS[fuelKey] || 'l';
    setFormData(prev => ({
      ...prev,
      fuelKey,
      unit: defaultUnit
    }));
    setIsSaved(false);
  };

  /**
   * Attach the source document. Uploaded on pick rather than on save — the same
   * order the process screen has used in production — so a failed upload is
   * surfaced while the user is still looking at the file they chose.
   */
  const handlePickFile = async (picked) => {
    if (!picked) return;
    if (picked.size > 10 * 1024 * 1024) {
      setUploadError(t('entry.uploadTooBig'));
      return;
    }
    if (!formData.buildingId) {
      setUploadError(t('entry.uploadNoBuilding'));
      return;
    }
    try {
      setUploading(true);
      setUploadError('');
      const uploadFormData = new FormData();
      uploadFormData.append('file', picked);
      uploadFormData.append('buildingId', formData.buildingId);

      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE}/api/scope1/invoices`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: uploadFormData
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Upload failed');
      }
      const data = await response.json();
      setUploadedInvoice(data);
      setFile(picked);
    } catch (err) {
      setUploadError(t('entry.uploadFailed', { message: err.message }));
    } finally {
      setUploading(false);
    }
  };

  // Combined calculate and save function
  // Step 1: calculate only, then move to "Rishiko & regjistro"
  const handleSaveEmissions = async () => {
    if (!formData.buildingId) {
      setBuildingValidationError(t('entry.uploadNoBuilding'));
      setStep(0);
      return;
    }

    try {
      setSaving(true);
      const token = localStorage.getItem('token');

      if (!token) {
        alert(t('s1dj.sessionExpired'));
        window.location.href = '/';
        return;
      }

      const calcResponse = await fetch(`${API_BASE}/api/emissions/scope1/calc`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          deviceId: parseInt(formData.buildingId),
          fuelKey: formData.fuelKey,
          period: formData.period,
          quantity: parseFloat(formData.quantity),
          unit: formData.unit
        })
      });

      if (calcResponse.status === 401) {
        alert(t('s1dj.sessionExpired'));
        localStorage.removeItem('token');
        window.location.href = '/';
        return;
      }

      if (!calcResponse.ok) throw new Error('Calculation failed');
      const calcData = await calcResponse.json();
      setCalculated(calcData);
      setBuildingValidationError('');
      setFlowError('');
      setStep(2);
    } catch (err) {
      setBuildingValidationError(t('s3p.error', { message: err.message }));
    } finally {
      setSaving(false);
    }
  };

  // Step 2: save from the review step
  const performSave = async () => {
    if (!calculated) return false;
    try {
      setSaving(true);
      const token = localStorage.getItem('token');
      if (!token) {
        alert(t('s1dj.sessionExpired'));
        window.location.href = '/';
        return false;
      }

      const payload = {
        buildingId: parseInt(formData.buildingId),
        fuelKey: formData.fuelKey,
        period: formData.period,
        periodType: formData.periodType,
        quantity: parseFloat(formData.quantity),
        unit: formData.unit,
        energy_kWh: calculated.energy_kWh,
        emissions_tCO2e: calculated.emissions_tCO2e,
        activityPurpose: activityPurpose || null
      };

      const saveResponse = await fetch(`${API_BASE}/api/emissions/scope1/records`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });

      if (!saveResponse.ok) {
        const errorData = await saveResponse.json();
        throw new Error(errorData.message || 'Save failed');
      }
      setIsSaved(true);
      setBuildingValidationError('');

      // Refresh on-screen summary so totals reflect the new record
      await fetchSummary(formData.period);
      return true;
    } catch (err) {
      setBuildingValidationError(t('s3p.error', { message: err.message }));
      return false;
    } finally {
      setSaving(false);
    }
  };

  const handleFlowSave = async () => {
    setFlowError('');
    const ok = await performSave();
    if (!ok) {
      setFlowError(t('s1dj.saveFailed'));
      return;
    }
    try {
      setCoverage(await fetchCoverage(formData.period));
    } catch (e) {
      setCoverage(null);
    }
    setLedgerKey(k => k + 1);
  };

  // "Shto aktivitet të ri" — same site and purpose, fresh activity.
  const handleFlowAddAnother = () => {
    setCalculated(null);
    setIsSaved(false);
    setFile(null);
    setUploadedInvoice(null);
    setUploadError('');
    setFormData(prev => ({ ...prev, fuelKey: '', quantity: '' }));
    setStep(1);
  };

  // "Mbaroj" — back to the top of the flow.
  const handleFlowFinish = () => {
    setCalculated(null);
    setIsSaved(false);
    setFile(null);
    setUploadedInvoice(null);
    setUploadError('');
    setFormData(prev => ({ ...prev, fuelKey: '', quantity: '' }));
    setStep(0);
  };

  const isSaveDisabled = !formData.buildingId || !formData.fuelKey || !formData.period || !formData.quantity ||
    parseFloat(formData.quantity) <= 0 || saving || isSaved;

  const isBusy = saving;

  // Biogenic fuels (Wood, Pellet) report CO₂ as a memo item under GHG Protocol —
  // it's tracked but excluded from the Scope 1 total.
  const selectedFuel = fuels.find(f => f.key === formData.fuelKey);
  const isBiogenicSelected = !!(selectedFuel && selectedFuel.biogenic);

  const unitLabel = formData.unit === 'l' ? t('s1dj.liters') : formData.unit === 'm3' ? 'm³' : formData.unit;

  return (
    <ErrorBoundary>
      <Shell>
        <div className="k2-wrap">
          {isBusy && <Loader />}

          <PageHeader
            title={t('stationary.title')}
            desc={t('stationary.desc')}
          >
            <Button variant="gh" icon={X.Doc} onClick={() => navigate('/records')}>
              {t('entry.viewRecords')}
            </Button>
          </PageHeader>

          <EntryStepper
            scope={1}
            step={step}
            setStep={setStep}
            facilities={facilities}
            siteId={formData.buildingId}
            onSite={(e) => { handleInputChange('buildingId', e.target.value); setBuildingValidationError(''); }}
            siteError={buildingValidationError}
            purposes={ACTIVITY_PURPOSES}
            purpose={activityPurpose}
            onPurpose={(e) => setActivityPurpose(e.target.value)}
            period={formData.period}
            mode={mode}
            setMode={setMode}
            ocrSupported={false}
            docLabel={t('stationary.doc')}
            file={file}
            onFile={handlePickFile}
            uploading={uploading}
            uploadNote={
              uploadError ? (
                <div style={{ marginTop: 10 }}><Callout tone="err">{uploadError}</Callout></div>
              ) : uploadedInvoice ? (
                <div style={{ marginTop: 10 }}>
                  <Callout tone="ok">{t('entry.uploadOk')}</Callout>
                </div>
              ) : null
            }
            onCalculate={handleSaveEmissions}
            calcDisabled={isSaveDisabled}
            calcLabel={t(saving ? 'entry.calculating' : 'entry.calc')}
            reviewRows={[
              { label: t('entry.building'), value: (facilities.find(f => String(f.id) === String(formData.buildingId)) || {}).name || '—' },
              activityPurpose ? { label: t('entry.purpose'), value: activityPurpose } : null,
              { label: t('stationary.fuel'), value: (selectedFuel || {}).label || formData.fuelKey || '—' },
              { label: t('stationary.quantity'), value: `${formData.quantity} ${unitLabel}` },
              { label: t('entry.period'), value: formData.period },
              { label: 'kWh', value: calculated ? `${Number(calculated.energy_kWh || 0).toFixed(0)} kWh` : '—' },
              { label: t('entry.document'), value: file ? file.name : t('entry.notAttached') }
            ]}
            result={{ value: calculated ? Number(calculated.emissions_tCO2e).toFixed(3) : '0.000', unit: t('common.tco2e') }}
            note={isBiogenicSelected ? t('stationary.biogenicNote') : null}
            saving={saving}
            error={flowError}
            onSave={handleFlowSave}
            saved={isSaved}
            successText={calculated
              ? t('stationary.success', { value: Number(calculated.emissions_tCO2e).toFixed(3), period: formData.period })
              : ''}
            coverage={buildCoverageChips('scope1', coverage)}
            nextStep={nextStepFor('scope1')}
            onAddAnother={handleFlowAddAnother}
            onFinish={handleFlowFinish}
            context={
              <>
                <CategoryContext
                  recordTypes={['scope1-stationary']}
                  categoryLabel={t('stationary.title')}
                  facilities={facilities}
                  refreshKey={ledgerKey}
                  onPickSite={(id) => { handleInputChange('buildingId', String(id)); setStep(1); }}
                />
                <MonthLedger
                  period={formData.period}
                  recordTypes={['scope1-stationary']}
                  buildings={facilities}
                  refreshKey={ledgerKey}
                  emptyHint={t('stationary.ledgerEmpty')}
                  onChanged={() => { fetchSummary(formData.period); setLedgerKey(k => k + 1); }}
                />
              </>
            }
          >
            {/* ── manual entry fields ── */}
            <div className="k2-g2">
              <Field label={t('stationary.fuel')} required>
                <Select value={formData.fuelKey} onChange={(e) => handleFuelChange(e.target.value)}>
                  <option value="">{t('stationary.fuelPlaceholder')}</option>
                  {fuels.map((f) => <option key={f.key} value={f.key}>{f.label}</option>)}
                </Select>
              </Field>

              <Field label={t('stationary.startMonth')} required>
                <Input type="month" value={formData.period} onChange={(e) => handleInputChange('period', e.target.value)} />
              </Field>
            </div>

            <Field label={t('stationary.quantity')} required>
              <UnitInput
                type="number"
                value={formData.quantity}
                onChange={(e) => handleInputChange('quantity', e.target.value)}
                unit={unitLabel}
              />
            </Field>

            {isBiogenicSelected && (
              <Callout tone="ok">
                <b>{t('stationary.biogenicTitle')}</b> {t('stationary.biogenicBody')}
              </Callout>
            )}

            {(summary.current?.biogenic_tCO2e || 0) > 0 && (
              <div style={{ fontSize: 11.5, color: 'var(--ok)', marginTop: 10 }}>
                {t('s1dj.biogenicSeparate', { period: formData.period, value: (summary.current.biogenic_tCO2e).toFixed(3) })}
              </div>
            )}
          </EntryStepper>
        </div>
      </Shell>
    </ErrorBoundary>
  );
};
